import React, { useEffect, useMemo, useState } from 'react';
import { getAdelantables } from '../../services/asignacionPistaService';
import { mapearPartidos, capitalizarModalidad } from './logica';
import PartidoCard from './PartidoCard';

const fechaCorta = (iso) => String(iso).slice(0, 10).split('-').reverse().join('/');

// Partidos de jornadas futuras que se pueden arrastrar al cuadrante de la jornada
// seleccionada. El partido mantiene su jornada; solo cambia dónde y cuándo se juega.
// `recarga` cambia cada vez que se refresca el cuadrante, para que los partidos ya
// colocados desaparezcan de la lista.
const AdelantarPartidos = ({ temporada, jornadasFuturas, rosterPorParticipante, fechaCuadrante, recarga }) => {
  const [jornadaOrigen, setJornadaOrigen] = useState('');
  const [modalidad, setModalidad] = useState('');
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  // Si la jornada elegida deja de ser futura (se cambió de jornada), se descarta.
  useEffect(() => {
    if (jornadaOrigen && !jornadasFuturas.includes(Number(jornadaOrigen))) setJornadaOrigen('');
  }, [jornadasFuturas, jornadaOrigen]);

  useEffect(() => {
    if (!temporada || !jornadaOrigen) { setLista([]); return undefined; }
    let vigente = true;
    setCargando(true);
    setError(null);
    getAdelantables(temporada, Number(jornadaOrigen))
      .then((datos) => { if (vigente) setLista(datos); })
      .catch((e) => { if (vigente) { setLista([]); setError(e.message); } })
      .finally(() => { if (vigente) setCargando(false); });
    return () => { vigente = false; };
  }, [temporada, jornadaOrigen, recarga]);

  // Solo las modalidades que aún tienen algún partido adelantable.
  const modalidades = useMemo(() => [...new Set(lista.map((p) => p.modalidad))].sort(), [lista]);
  useEffect(() => {
    // Al cambiar de jornada se conserva la modalidad si la nueva también la tiene;
    // hasta que no llega la lista nueva no se sabe, así que no se toca mientras carga.
    if (jornadaOrigen && !cargando && modalidad && !modalidades.includes(modalidad)) setModalidad('');
  }, [modalidades, modalidad, jornadaOrigen, cargando]);

  const tarjetas = useMemo(
    () => mapearPartidos(lista.filter((p) => p.modalidad === modalidad), rosterPorParticipante, { adelantables: true }),
    [lista, modalidad, rosterPorParticipante],
  );

  if (!jornadasFuturas.length) return null;

  return (
    <section className="cp-pool cp-adelantar" aria-labelledby="cp-adelantar-titulo">
      <h4 id="cp-adelantar-titulo">Adelantar partidos de jornadas futuras a esta jornada</h4>
      <p className="cp-adelantar-nota">
        El partido mantiene su jornada; solo cambia la fecha en que se juega
        {fechaCuadrante ? ` (${fechaCorta(fechaCuadrante)})` : ' (esta jornada aún no tiene fecha: se queda la actual)'}.
      </p>
      <label className="cp-jornada-selector">
        Jornada
        <select value={jornadaOrigen} onChange={(e) => setJornadaOrigen(e.target.value)}>
          <option value="">— Elegir —</option>
          {jornadasFuturas.map((j) => <option key={j} value={j}>Jornada {j}</option>)}
        </select>
      </label>
      {jornadaOrigen && (
        <label className="cp-jornada-selector">
          Modalidad
          <select value={modalidad} onChange={(e) => setModalidad(e.target.value)} disabled={!modalidades.length}>
            <option value="">— Elegir —</option>
            {modalidades.map((m) => <option key={m} value={m}>{capitalizarModalidad(m)}</option>)}
          </select>
        </label>
      )}
      {error && <p className="cp-aviso cp-aviso-error">{error}</p>}
      {cargando && <p>Cargando…</p>}
      {jornadaOrigen && !cargando && !error && !modalidades.length && (
        <p className="cp-pool-vacio">No hay partidos adelantables en la jornada {jornadaOrigen}.</p>
      )}
      <div className="cp-pool-lista">
        {tarjetas.map((p) => <PartidoCard key={p.id} partido={p} editable conflicto={null} />)}
      </div>
    </section>
  );
};

export default AdelantarPartidos;
